/**
 * PrelaunchGate , site-wide kill switch that funnels every public visitor
 * to /waitlist until launch day. Admins (and anyone the admins approve from
 * the waitlist) can still log in and access the full site.
 *
 * How it works:
 *   1. Reads `process.env.REACT_APP_PRELAUNCH_MODE` (default "true" in this
 *      build). When "false" the gate becomes a no-op , flip the env var on
 *      launch day and redeploy.
 *   2. The hidden /home route (NEVER linked publicly) sets a localStorage
 *      flag `iw_prelaunch_unlocked` and redirects to `/`. Once the flag is
 *      set on a device, that device sees the full site. Admins share the
 *      `/home` URL by word-of-mouth.
 *   3. The ONLY way to bypass the gate on a given device is to visit the
 *      hidden /home URL (which drops the unlock flag in localStorage).
 *      Logged-in admins are NOT auto-bypassed , they too must hit /home
 *      once per device, otherwise opening the preview URL just funnels
 *      them to the waitlist like every other visitor. (This is intentional
 *      so admins see the public experience by default.)
 *
 * Routes that ALWAYS work (legal/operational):
 *   /waitlist          , the only public destination
 *   /waitlist-confirmation, post-signup confirmation page
 *   /login             , admins + approved waitlist users sign in here
 *   /forgot-password   , password reset is always available
 *   /privacy /terms /coppa-notice /do-not-sell , legal pages must remain accessible
 *   /seo/sitemap.xml /seo/robots.txt , crawler endpoints (server-side anyway)
 *   /admin-*           , admin dashboards remain accessible (auth-gated)
 *   /home              , the secret unlock
 */
import React from "react";
import { Navigate, useLocation } from "react-router-dom";

const UNLOCK_KEY = "iw_prelaunch_unlocked";
const TOKEN_KEY = "isaiah_token";  // mirror of App.js auth key, intentional duplicate to avoid circular import
const USER_KEY = "isaiah_user";    // belt-and-suspenders: a present user blob is also "authed"

// Build-time fallback. The live `mode` from `/api/launch-mode` overrides
// this (cached in localStorage under LAUNCH_MODE_CACHE_KEY).
const PRELAUNCH_MODE_BUILD = (process.env.REACT_APP_PRELAUNCH_MODE ?? "true").toLowerCase() !== "false";
const LAUNCH_MODE_CACHE_KEY = "iw_launch_mode_cache";
const LAUNCH_MODE_TTL_MS = 60 * 1000;   // refetch at most once a minute

/**
 * Read the current launch mode synchronously. Order:
 *   1. If a fresh value exists in localStorage (<60s old), use it.
 *   2. Otherwise fall back to the build-time env var.
 *
 * Networking happens on a separate `kickRefetchLaunchMode()` call,
 * scheduled at module load (see bottom of file). This keeps the gate
 * decision SYNCHRONOUS so we don't need to render a loading flash.
 */
function readCachedLaunchMode() {
  try {
    const raw = localStorage.getItem(LAUNCH_MODE_CACHE_KEY);
    if (!raw) return null;
    const { mode, ts } = JSON.parse(raw);
    if (!mode) return null;
    if (Date.now() - ts > LAUNCH_MODE_TTL_MS * 30) return null;  // hard expire after 30 min
    return mode;
  } catch { return null; }
}

function effectiveMode() {
  return readCachedLaunchMode() || (PRELAUNCH_MODE_BUILD ? "prelaunch" : "postlaunch");
}

// Module-singleton: only one in-flight fetch at a time.
let _refetchPromise = null;
export function kickRefetchLaunchMode() {
  if (_refetchPromise) return _refetchPromise;
  _refetchPromise = (async () => {
    try {
      const base = process.env.REACT_APP_BACKEND_URL || "";
      const r = await fetch(`${base}/api/launch-mode`, { cache: "no-store" });
      if (r.ok) {
        const j = await r.json();
        if (j && j.mode) {
          localStorage.setItem(
            LAUNCH_MODE_CACHE_KEY,
            JSON.stringify({ mode: j.mode, ts: Date.now() }),
          );
        }
      }
    } catch { /* offline, keep using cache or build-time fallback */ }
    finally { _refetchPromise = null; }
  })();
  return _refetchPromise;
}

// Kick a refetch on first import so visitors with stale cache catch up
// quickly. The localStorage write triggers a re-render via the visibility
// listener below.
if (typeof window !== "undefined") {
  kickRefetchLaunchMode();
  // Also refresh whenever the tab becomes visible again, covers the
  // case where a visitor leaves the tab open across an admin flip.
  document.addEventListener("visibilitychange", () => {
    if (!document.hidden) kickRefetchLaunchMode();
  });
}

// Routes that bypass the gate entirely. /home is the secret unlock. Legal +
// auth pages stay open so admins (and approved waitlist users) can sign in.
const ALLOWED_PATHS = new Set([
  "/waitlist",
  "/waitlist-confirmation",
  "/login",
  "/signup",                 // /home routes new founders here, must stay open
  "/forgot-password",
  "/reset-password",
  "/privacy",
  "/terms",
  "/coppa-notice",
  "/do-not-sell",
  "/safety",
  "/report-safety",
  "/contact",
  "/home",
  "/voice-studio",  // public voice picker so admins can preview from any browser tab
  "/learning-curriculum",  // public curriculum overview (marketing landing, Phase 4)
  "/learning-features",    // public premium-suite landing page (linked from Footer + Premium modal)
]);

const ALLOWED_PREFIXES = [
  "/admin-",        // admin dashboards remain accessible (auth-gated)
  "/admin/",        // /admin/ops-checklist + similar dashboard sub-routes (May 2026)
  "/founder/",      // /founder/:token bulletproof entry, see FounderEntry below
  "/approve-link/", // /approve-link/:token  parent→child claim approval
  "/confirm-kid/",  // /confirm-kid/:token  parent confirmation for kid_explorer signup
  "/u/",            // /u/:username  public profile cards (viral surface)
  "/shared-activity/", // /shared-activity/:token  read-only tutor activity view
  "/payment/",      // /payment/success, Stripe redirects users back here after every checkout
  "/api/",          // never matches a frontend route, defensive
];

/**
 * /home , the universal pre-launch entry URL we hand out to every founder
 * (in welcome emails, SMS, share links). It's a smart router:
 *
 *   • Always drops the device-level unlock flag so the rest of the site
 *     works after this single visit.
 *   • If the URL carries a `?founder_token=...` (the parameter the early-
 *     access email button passes), we forward straight to
 *     `/signup?founder_token=...` so the founder lands on the password-
 *     creation form with their pre-approval intact.
 *   • If the visitor is already authenticated (has `isaiah_token` in
 *     local/session storage) we take them to `/` (the in-product home
 *     page / dashboard) , the URL becomes their permanent bookmark
 *     during pre-launch.
 *   • Otherwise the visitor is a not-yet-signed-up founder who clicked
 *     the link from an email/SMS , forward to `/signup` so they finish
 *     creating their account. If they exit without signing up, the next
 *     `/home` visit just funnels them right back to `/signup`. Once
 *     signed in, that same URL flips automatically to `/`.
 */
export function PrelaunchHome() {
  // Use a hard `window.location.replace(...)` instead of React Router's
  // `<Navigate>` to guarantee the URL bar matches what we want , chained
  // Navigate-to-a-search-string was dropping the founder_token query in
  // some browser/StrictMode combinations, so SignupPage couldn't read it.
  // eslint-disable-next-line react-hooks/rules-of-hooks
  React.useEffect(() => {
    try { localStorage.setItem(UNLOCK_KEY, "1"); } catch { /* ignore */ }
    let founderToken = null;
    try {
      const params = new URLSearchParams(window.location.search);
      founderToken = params.get("founder_token");
    } catch { /* ignore */ }
    // A founder-invite link (with `?founder_token=...`) still forwards to
    // the signup form so the token can be redeemed. Otherwise the visitor
    // lands on the actual HomePage so they can browse the site. We stash
    // an "insider intent" flag in localStorage so when they later click
    // Sign Up, SignupPage can opt them into the waitlist-bypass insider
    // flow (sequential founder position, no waitlist queue).
    if (founderToken) {
      window.location.replace(`/signup?founder_token=${encodeURIComponent(founderToken)}`);
      return;
    }
    try { localStorage.setItem("iw_insider_intent", "1"); } catch { /* ignore */ }
    window.location.replace("/");
  }, []);
  return (
    <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:"#0F1428",color:"#FFFFFF",fontFamily:"system-ui,sans-serif"}}>
      <p>Loading…</p>
    </div>
  );
}

/**
 * /founder/:token , bulletproof fallback entry URL. When email-client
 * link sanitizers (Yahoo's "view in safe mode", corporate trackers,
 * privacy extensions) strip the `?founder_token=...` query string from
 * `/home?founder_token=...`, the founder lands on bare `/home` , which
 * (if they happened to be signed in to a different account, e.g. admin
 * testing) routes them to `/` and they never see the signup form.
 *
 * This path encodes the token directly in the URL path so it survives
 * any query-stripping tracker. The email button now uses
 * `/founder/<token>` as a backup; some clients may still rewrite to
 * `/home?founder_token=...`. Either way the founder ends up at
 * `/signup?founder_token=...`.
 *
 * Implementation note: we use `window.location.replace(...)` instead of
 * React Router's `<Navigate>` because chained Navigate-to-a-search-string
 * is dropping the query string in some browsers/StrictMode renders.
 * A hard navigation guarantees the URL bar matches what we want and
 * SignupPage's `useSearchParams` reads the founder_token correctly.
 */
export function FounderEntry() {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  React.useEffect(() => {
    try { localStorage.setItem(UNLOCK_KEY, "1"); } catch { /* ignore */ }
    let token = "";
    try {
      const m = window.location.pathname.match(/^\/founder\/([^/?#]+)/);
      if (m) token = decodeURIComponent(m[1]);
    } catch { /* ignore */ }
    const qs = token ? `?founder_token=${encodeURIComponent(token)}` : "";
    window.location.replace(`/signup${qs}`);
  }, []);
  return (
    <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:"#0F1428",color:"#FFD700",fontFamily:"system-ui,sans-serif"}}>
      <p>Setting up your founder account…</p>
    </div>
  );
}

/**
 * isPrelaunchUnlocked , exposed for components that want to render
 * different copy/CTAs while in pre-launch (e.g. the homepage).
 *
 * Returns true when:
 *   - Launch mode flipped past pre-launch, OR
 *   - The device hit /home and persisted the unlock flag, OR
 *   - The user is authenticated (auth supersedes the device gate, so
 *     logged-in founders never see the locked chrome regardless of
 *     which device they're on or whether they used /home first).
 */
export function isPrelaunchUnlocked() {
  if (effectiveMode() !== "prelaunch") return true;
  try {
    if (localStorage.getItem(UNLOCK_KEY) === "1") return true;
    if (localStorage.getItem(TOKEN_KEY) || sessionStorage.getItem(TOKEN_KEY)) return true;
  } catch { /* ignore */ }
  if (isGuestRoamActive()) return true;
  return false;
}

// Round 38 part 8, Guest Roam Mode.
// Lets visitors browse the site for 5 minutes without signing up. The
// homepage's "Roam as a guest" button calls `startGuestRoam()` which
// stamps an expiration timestamp into localStorage; PrelaunchGate
// honors it like the device-unlock flag for the duration of the timer.
const GUEST_UNTIL_KEY = "iw_guest_until";
export const GUEST_ROAM_DURATION_MS = 5 * 60 * 1000;
export function startGuestRoam() {
  // PRIVACY: a guest is a blank slate. Purge ANY leftover account/learner
  // state from a previously signed-in user on this device so guest mode never
  // reveals another person's info (e.g. a child's grade in the Learn tools).
  // NOTE: we intentionally do NOT touch sessionStorage isaiah_token/isaiah_user
  // here, the guest's own session token is written there right after this call.
  try {
    localStorage.removeItem("isaiah_active_learner");
    localStorage.removeItem("academy_active_child_id");
    localStorage.removeItem("isaiah_user");
    localStorage.removeItem("isaiah_token");
    sessionStorage.removeItem("academy_active_child_id");
    sessionStorage.removeItem("academy_active_child_profile");
    localStorage.removeItem("academy_active_child_profile");
  } catch { /* ignore */ }
  try { localStorage.setItem(GUEST_UNTIL_KEY, String(Date.now() + GUEST_ROAM_DURATION_MS)); } catch { /* ignore */ }
}
export function guestRoamRemainingMs() {
  try {
    const raw = localStorage.getItem(GUEST_UNTIL_KEY);
    if (!raw) return 0;
    const until = parseInt(raw, 10);
    if (!Number.isFinite(until)) return 0;
    return Math.max(0, until - Date.now());
  } catch { return 0; }
}
export function isGuestRoamActive() {
  return guestRoamRemainingMs() > 0;
}
export function clearGuestRoam() {
  try { localStorage.removeItem(GUEST_UNTIL_KEY); } catch { /* ignore */ }
}

export default function PrelaunchGate({ children }) {
  // PUBLIC-OPEN PRELAUNCH (2026-06): the funnel/gate is intentionally disabled.
  // Everyone, logged in or not, reaches the site (homepage) directly with no
  // special route or unlock. Signups still receive the Founder badge (handled
  // server-side by launch mode). A launch banner / scarcity prompt will be
  // added later WITHOUT re-enabling any redirect gate.
  return children;
}
